import { StatusBar } from 'expo-status-bar';
import { StyleSheet, View, KeyboardAvoidingView, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text, TextInput, Button, Surface, Snackbar, IconButton } from 'react-native-paper';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { GradientBackground } from "../components/GradientBackground"
import { Header } from "../components/Header"
export function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { signIn, resetPassword } = useAuth();
  const { theme, isDarkMode, toggleTheme } = useTheme();

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await signIn(email, password);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await resetPassword(email);
      setError('Password reset email sent! Check your inbox.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <GradientBackground>
        <Header showLogout={false} showThemeToggle={true} />
        <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : "height"} style={styles.content}>
          <View style={styles.header}>
            <Text style={[styles.title, { color: theme.dark ? '#FFFFFF' : '#000000' }]}>
              Transmission Asset Management System
            </Text>
            <IconButton
              icon={({ size, color }) => (
                <MaterialCommunityIcons
                  name={isDarkMode ? 'weather-sunny' : 'weather-night'}
                  size={size}
                  color={color}
                />
              )}
              size={24}
              iconColor={theme.dark ? '#FFFFFF' : '#000000'}
              onPress={toggleTheme}
              style={styles.themeToggle}
            />
          </View>
          <Text style={[styles.subtitle, { color: theme.dark ? '#FFFFFF' : '#000000' }]}>
            The Digital Transmission Line
          </Text>

          <Surface style={[styles.loginContainer, { backgroundColor: theme.colors.surface }]}>
            <TextInput
              label="Email"
              value={email}
              onChangeText={(text) => {
                setEmail(text);
                setError('');
              }}
              mode="outlined"
              style={styles.input}
              keyboardType="email-address"
              autoCapitalize="none"
              disabled={loading}
              theme={theme}
            />
            <TextInput
              label="Password"
              value={password}
              onChangeText={(text) => {
                setPassword(text);
                setError('');
              }}
              mode="outlined"
              style={styles.input}
              secureTextEntry
              disabled={loading}
              theme={theme}
            />
            <View style={styles.buttonContainer}>
              <Button
                mode="contained"
                onPress={handleLogin}
                loading={loading}
                disabled={loading}
                style={styles.loginButton}
              >
                Login
              </Button>
              <Button
                mode="text"
                onPress={handleForgotPassword}
                disabled={loading}
                style={styles.forgotButton}
              >
                Forgot Password?
              </Button>
            </View>
          </Surface>
          <StatusBar style={theme.dark ? 'light' : 'dark'} />
        </KeyboardAvoidingView>
      </GradientBackground>
      <Snackbar
        visible={!!error}
        onDismiss={() => setError('')}
        action={{
          label: 'Close',
          onPress: () => setError(''),
        }}
        theme={theme}
      >
        {error}
      </Snackbar>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  gradient: {
    flex: 1,
    width: '100%',
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },
  themeToggle: {
    marginLeft: 10,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 18,
    textAlign: 'center',
    opacity: 0.9,
    marginBottom: 30,
    textShadowColor: 'rgba(0, 0, 0, 0.2)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  loginContainer: {
    padding: 20,
    borderRadius: 10,
    width: '100%',
    maxWidth: 400,
    elevation: 4,
  },
  input: {
    marginBottom: 12,
  },
  buttonContainer: {
    marginTop: 24,
    gap: 12,
  },
  loginButton: {
    paddingVertical: 6,
  },
  forgotButton: {
    marginTop: 8,
  },
});
